<script setup>
/* global axios */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useModulePermissions } from 'dashboard/composables/useModulePermissions';
import AModal from './AModal.vue';
import { PLAN_TYPES } from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;
// Gating de ações por FUNÇÃO: esconde criar/editar/excluir conforme a permissão
// CRUD do módulo appointments (o backend recusa de todo jeito; o botão some).
const { moduleCan } = useModulePermissions();
const canCreateAppointments = computed(() =>
  moduleCan('create', 'appointments')
);
const canEditAppointments = computed(() => moduleCan('update', 'appointments'));
const canDeleteAppointments = computed(() =>
  moduleCan('delete', 'appointments')
);

const plans = ref([]);
const show = ref(false);
const form = ref({});

// tipos de parceiro definidos pelo usuário (CRUD próprio) + os padrão (PLAN_TYPES)
const partnerTypes = ref([]);
const typesModal = ref(false);
const newTypeName = ref('');
const typeError = ref('');

// Os tipos padrão agora são registros semeados no backend, então o select usa só
// a tabela. PLAN_TYPES fica só p/ traduzir valores ANTIGOS de parceiros já salvos.
const typeOptions = computed(() =>
  partnerTypes.value.map(tp => ({ value: tp.name, label: tp.name }))
);
// valor built-in antigo -> nome do tipo semeado (p/ pré-selecionar ao editar)
const BUILTIN_TO_NAME = {
  particular: 'Particular',
  convenio: 'Parceiro',
  plano: 'Plano de Saúde',
};

const load = async () => {
  plans.value = (await axios.get(`${base}/health_plans`)).data.data || [];
};
const loadTypes = async () => {
  partnerTypes.value =
    (await axios.get(`${base}/partner_types`)).data.data || [];
};
const addType = async () => {
  const name = newTypeName.value.trim();
  if (!name) return;
  typeError.value = '';
  try {
    await axios.post(`${base}/partner_types`, { name });
    newTypeName.value = '';
    await loadTypes();
  } catch (e) {
    typeError.value =
      e?.response?.data?.error ||
      t('ATENDIMENTOS.partners.partnerTypes.saveError');
  }
};
const saveType = async tp => {
  if (!tp.name?.trim()) return;
  await axios.patch(`${base}/partner_types/${tp.id}`, { name: tp.name });
  await loadTypes();
};
const removeType = async tp => {
  if (
    !window.confirm(
      t('ATENDIMENTOS.partners.partnerTypes.deleteConfirm', { name: tp.name })
    )
  )
    return;
  await axios.delete(`${base}/partner_types/${tp.id}`);
  await loadTypes();
};
const open = (p = null) => {
  if (p) {
    const data = { ...p };
    // normaliza valor built-in antigo p/ o nome (pra o select pré-selecionar)
    if (BUILTIN_TO_NAME[data.plan_type])
      data.plan_type = BUILTIN_TO_NAME[data.plan_type];
    form.value = data;
  } else {
    form.value = {
      name: '',
      plan_type: typeOptions.value[0]?.value || '',
      payment_days: 30,
      requires_auth: false,
      sessions_limit: '',
      notes: '',
      is_active: true,
    };
  }
  show.value = true;
};
const save = async () => {
  if (!form.value.name?.trim()) return;
  if (form.value.id)
    await axios.patch(`${base}/health_plans/${form.value.id}`, form.value);
  else await axios.post(`${base}/health_plans`, form.value);
  show.value = false;
  await load();
};
const remove = async p => {
  if (
    !window.confirm(t('ATENDIMENTOS.partners.deleteConfirm', { name: p.name }))
  )
    return;
  await axios.delete(`${base}/health_plans/${p.id}`);
  await load();
};
const typeLabel = type => {
  const pt = PLAN_TYPES.find(x => x.value === type);
  return pt ? t(pt.labelKey) : type || '';
};

onMounted(() => {
  load();
  loadTypes();
});
</script>

<template>
  <div class="p-6">
    <div class="flex justify-end gap-2 mb-4">
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="
          typeError = '';
          typesModal = true;
        "
      >
        {{ t('ATENDIMENTOS.partners.partnerTypes.manage') }}
      </button>
      <button
        v-if="canCreateAppointments"
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="open()"
      >
        {{ t('ATENDIMENTOS.partners.newPartner') }}
      </button>
    </div>
    <div class="overflow-hidden bg-n-solid-2 border rounded-xl border-n-weak">
      <table class="min-w-full text-sm divide-y divide-n-weak">
        <thead class="bg-n-alpha-1">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.partners.columns.name') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.partners.columns.type') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10 text-center"
            >
              {{ t('ATENDIMENTOS.partners.columns.paymentDaysFull') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10 text-right"
            >
              {{ t('ATENDIMENTOS.partners.columns.actions') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="h in plans" :key="h.id" class="hover:bg-n-alpha-1">
            <td class="px-4 py-3 text-n-slate-12">{{ h.name }}</td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ typeLabel(h.plan_type) }}
            </td>
            <td class="px-4 py-3 text-center text-n-slate-11">
              {{ h.payment_days }}
            </td>
            <td class="px-4 py-3 text-right">
              <button
                v-if="canEditAppointments"
                class="text-xs text-n-blue-11 hover:underline"
                @click="open(h)"
              >
                {{ t('ATENDIMENTOS.partners.edit') }}
              </button>
              <button
                v-if="canDeleteAppointments"
                class="ml-2 text-xs text-n-ruby-11 hover:underline"
                @click="remove(h)"
              >
                {{ t('ATENDIMENTOS.patients.delete') }}
              </button>
            </td>
          </tr>
          <tr v-if="!plans.length">
            <td colspan="4" class="px-4 py-8 text-center text-n-slate-10">
              {{ t('ATENDIMENTOS.partners.empty') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AModal
      v-if="show"
      :title="
        form.id
          ? t('ATENDIMENTOS.partners.modal.editTitle')
          : t('ATENDIMENTOS.partners.modal.newTitle')
      "
      size="sm"
      @close="show = false"
    >
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.partners.modal.namefield')
        }}</label
        ><input
          v-model="form.name"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.partners.modal.typeField')
        }}</label
        ><select
          v-model="form.plan_type"
          class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option
            v-for="opt in typeOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.partners.modal.paymentDaysField')
          }}</label
          ><input
            v-model="form.payment_days"
            type="number"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.partners.modal.sessionsLimit')
          }}</label
          ><input
            v-model="form.sessions_limit"
            type="number"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <label class="flex items-center gap-2 text-sm"
        ><input v-model="form.requires_auth" type="checkbox" />
        {{ t('ATENDIMENTOS.partners.modal.requiresAuth') }}</label
      >
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.partners.modal.cancel') }}</button
        ><button
          v-if="form.id ? canEditAppointments : canCreateAppointments"
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="save"
        >
          {{ t('ATENDIMENTOS.partners.modal.save') }}
        </button>
      </template>
    </AModal>

    <AModal
      v-if="typesModal"
      :title="t('ATENDIMENTOS.partners.partnerTypes.title')"
      size="sm"
      @close="typesModal = false"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('ATENDIMENTOS.partners.partnerTypes.hint') }}
      </p>
      <div class="flex gap-2">
        <input
          v-model="newTypeName"
          :placeholder="t('ATENDIMENTOS.partners.partnerTypes.placeholder')"
          class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          @keyup.enter="addType"
        />
        <button
          v-if="canCreateAppointments"
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="addType"
        >
          {{ t('ATENDIMENTOS.partners.partnerTypes.add') }}
        </button>
      </div>
      <p v-if="typeError" class="text-xs text-n-ruby-11">{{ typeError }}</p>
      <ul class="divide-y divide-n-weak">
        <li
          v-for="tp in partnerTypes"
          :key="tp.id"
          class="flex items-center gap-2 py-2"
        >
          <input
            v-model="tp.name"
            class="flex-1 px-2 py-1 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @blur="saveType(tp)"
            @keyup.enter="saveType(tp)"
          />
          <button
            v-if="canDeleteAppointments"
            class="text-xs text-n-ruby-11 hover:underline"
            @click="removeType(tp)"
          >
            {{ t('ATENDIMENTOS.partners.partnerTypes.remove') }}
          </button>
        </li>
        <li
          v-if="!partnerTypes.length"
          class="py-6 text-sm text-center text-n-slate-10"
        >
          {{ t('ATENDIMENTOS.partners.partnerTypes.empty') }}
        </li>
      </ul>
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95"
          @click="typesModal = false"
        >
          {{ t('ATENDIMENTOS.partners.partnerTypes.done') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
